import { CustomElement, OverrideCustomElementConfig } from "@cervval.js/client-html5-core/src/components/customElement.decorator";
import { TypedHtmlElements } from "@cervval.js/client-html5-core/src/components/typedHtmlElements.decorator";
import { TypedWebComponentOptions, WebComponentOptions } from "@cervval.js/client-html5-core/src/components/typedWebComponentOptions.decorator";
import { FLEX_CONTAINER_COLUMN } from "@cervval.js/client-html5-core/src/tools/cssClasses.tools";
import { KeyboardInput } from "@cervval.js/client-html5-core/src/tools/dom.tools";
import { ensureLoaded } from "@cervval.js/common/src/tools/object.tools";
import { CjsInput, InputOptions } from "../../components/inputs/input";
import { CjsEditor, EditorOptions, defaultEditorOptions } from "../editor";

ensureLoaded(CjsInput);

export type InputNumberEditorOptions = EditorOptions;

export const defaultInputNumberEditorOptions: InputNumberEditorOptions = {
    ...defaultEditorOptions
};


/**
 * Class representing a component with an input text
 */
@CustomElement({
    name: "cjs-input-number-editor",
    style: require("./inputNumberEditor.scss"),
    template: require("./inputNumberEditor.html"),
    elementCssClasses: ["cjs-editor", FLEX_CONTAINER_COLUMN]
})
export class CjsInputNumberEditor extends CjsEditor<number> {

    /** Attributes options of InputNumberEditor */
    @TypedWebComponentOptions(defaultInputNumberEditorOptions)
    public readonly options!: WebComponentOptions<InputNumberEditorOptions>;

    /** Shortcut for options of Input */
    public get inputOptions(): WebComponentOptions<InputOptions<number>> {
        return this.getInput().options;
    }

    /** components */
    @TypedHtmlElements()
    private readonly _components!: {
        inputNumber: CjsInput<number>
    };

    constructor(options?: Partial<InputNumberEditorOptions>, inputOptions?: Partial<InputOptions<number>>, customElementOptions?: OverrideCustomElementConfig) {
        super(customElementOptions);
        this.options.setWithoutRefresh(options);
        this.inputOptions.setWithoutRefresh(inputOptions);
    }

    /** Get input of Editor, where user set data */
    public getInput(): CjsInput<number> {
        return this._components.inputNumber;
    }

    /**
     * Initialize components, input components and callbacks after the element is added to the dom and _components is ready
     */
    public override init(): void {
        super.init();

        // Callback for input components
        this.getInput().changeEvent.on((event: Event) => this._onInputValueChange(event));
        this.getInput().addEventListener("keyup", (e) => {
            if (e.key === KeyboardInput.ENTER) {
                this._onInputValueChange(e);
            }
        });

    }

    /** Refresh component display */
    public override refresh(): void {

        // Force input type number
        this.inputOptions.setWithoutRefresh({ inputType: "number" });

        // Refresh
        super.refresh();

        // Set options
        this.inputOptions.setWithoutRefresh({ value: this._value });
        this.getInput().refresh();
    }

    /**
     * Update the input value from stored value
     */
    protected _refreshInputValue(): void {
        this.inputOptions.setAndRefreshIfChanged({ value: this._value });
    }

    /**
     * Gets the value corresponding to the current input value
     */
    protected _getValueFromInputComponent(): number | undefined {
        return this.getInput().value;
    }

    /** Set formCheckInput as validation form element */
    public override getValidationFormElement(): HTMLElement {
        return this.getInput().input;
    }

    /** Update disabled attribute on input component */
    protected _refreshDisabled(): void {
        this.inputOptions.setAndRefreshIfChanged({ disabled: this.disabled });
    }
}